import { Box, Button, Divider, IconButtonProps, Popover } from "@mui/material";
import { Loading } from "app/components/Loading";
import { usePopoverActions } from "app/hooks/popover/usePopoverActions";
import { useRouter } from "app/tools/router/hooks/useRouter";
import { useCallback } from "react";
import { useUserQuery } from "User/api/readUser/hook";
import { useAuthTokenContext } from "User/Authentication/context";

import { AccountDetails } from "./components/AccountDetails";
import { AccountMenu } from "./components/AccountMenu";
import { AvatarIcon } from "./components/AvatarIcon";
import { AccountMenuItem } from "./types";

export type AccountPopoverProps = IconButtonProps & {
  menuItems?: AccountMenuItem[];
};

export function AccountPopover({
  menuItems = [],
  sx,
  ...other
}: AccountPopoverProps) {
  const router = useRouter();
  const authContext = useAuthTokenContext();
  const { data: user, isError, isPending } = useUserQuery();

  const { closePopover, openPopover, popoverElementAnchor } =
    usePopoverActions();

  const onMenuItemSelected = useCallback(
    (path: string) => {
      closePopover();
      router.push(path);
    },
    [closePopover, router],
  );

  if (isPending) {
    return <Loading size={40} />;
  }

  if (isError) {
    throw new Error("Nepodařilo se načíst data přihlášeného uživatele.");
  }

  return (
    <>
      <AvatarIcon openPopover={openPopover} sx={sx} user={user} {...other} />

      <Popover
        anchorEl={popoverElementAnchor}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        onClose={closePopover}
        open={!!popoverElementAnchor}
        slotProps={{
          paper: {
            sx: { width: 200 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <AccountDetails user={user} />
        <Divider sx={{ borderStyle: "dashed" }} />

        {menuItems.length > 0 && (
          <>
            <AccountMenu
              menuItems={menuItems}
              onMenuItemSelected={onMenuItemSelected}
            />
            <Divider sx={{ borderStyle: "dashed" }} />
          </>
        )}

        <Box sx={{ p: 1 }}>
          <Button
            color="error"
            fullWidth
            onClick={authContext?.logout}
            size="medium"
            variant="text"
          >
            Logout
          </Button>
        </Box>
      </Popover>
    </>
  );
}
